/**
 * useAdapterStrings — i18n-resolved strings for the popular-odds adapter.
 *
 * Shared between `<PopularOddsWidget>` (article + video pages) and
 * `<MultiMarketStack>` (kamp-side per-market sections, #409 Phase 5b).
 * Centralising the keys keeps adapter copy consistent across surfaces;
 * a tenant that translates `odds.over` once now sees the new label in
 * every market card.
 *
 * All `defaultValue`s are English — locales without a bundled translation
 * fall through to readable English copy.
 */
import { useTranslation } from '../i18n/index.js';
import type { AdapterStrings } from './popularOddsAdapter.js';

export function useAdapterStrings(): AdapterStrings {
  const { t } = useTranslation();
  return {
    matchWinner: t('odds.matchWinner', { defaultValue: 'Match winner' }),
    btts: t('odds.bothTeamsToScore', {
      defaultValue: 'Both teams to score',
    }),
    bttsSub: t('odds.bttsSub', { defaultValue: 'BTTS — Yes/No' }),
    yes: t('odds.yes', { defaultValue: 'Yes' }),
    no: t('odds.no', { defaultValue: 'No' }),
    over: (line: string) =>
      t('odds.over', { defaultValue: 'Over {{line}}', line }),
    under: (line: string) =>
      t('odds.under', { defaultValue: 'Under {{line}}', line }),
    totalGoals: (line: string) =>
      t('odds.totalGoals', { defaultValue: 'Over/Under {{line}}', line }),
    totalGoalsSub: (line: string) =>
      t('odds.totalGoalsSub', { defaultValue: 'Line {{line}}', line }),
    asianHandicap: (signedLine: string) =>
      t('odds.asianHandicap', {
        defaultValue: signedLine ? 'AH {{line}}' : 'Asian handicap',
        line: signedLine,
      }),
    asianHandicapSub: (signedLine: string) =>
      t('odds.asianHandicapSub', {
        defaultValue: 'Asian handicap {{line}}',
        line: signedLine,
      }),
    homeFallback: t('common.home', { defaultValue: 'Home' }),
    awayFallback: t('common.away', { defaultValue: 'Away' }),
    doubleChance: t('odds.doubleChance', { defaultValue: 'Double chance' }),
    doubleChanceSub: t('odds.doubleChanceSub', {
      defaultValue: '1X / 12 / X2',
    }),
  };
}
